/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {jobNotificationUuid} from '@ion/applications/src/notifications/state';
import {translate} from '@ion/core/src/locale/locale';
import {AlertSeverity} from '@ion/core/src/types/entities';
import {Notification} from '@ion/dls/src/types/extensions';
import {jobStatusBadge, jobStatusLabel} from './jobStatusBadge';
import {JobNotificationEntry} from './WatchedJobs';

/**
 * The card's secondary line: what happened, and why if the service said.
 *
 * Both halves are display-only, and either can be absent - a payload carrying
 * no `progress` leaves the line empty rather than rendering `undefined`. The
 * status half is the translated label, not the raw value, so it cannot
 * disagree with the badge above it.
 */
export const statusSubText = ({status, reason}: Partial<JobNotificationEntry>): string => (
  [jobStatusLabel(status), reason].filter(Boolean).join(' - ')
);

/**
 * Adapts one stored job notification into the shared panel's `Notification`
 * shape, so job rows inherit the drawer, the date grouping, the accordions,
 * selection, the confirmation dialog and the empty state rather than getting a
 * second renderer.
 *
 * Every field is here for a reason, and four of them are load-bearing in ways
 * the panel fails *silently* without:
 *
 * - **`id` and `uuid` both.** `id` is what the clear filter and the unread
 *   count match on; `uuid` is what selection and expand state match on.
 *   Carrying only one fails silently, and both ways round: with no `uuid`,
 *   `undefined === undefined` makes every row match, so ticking one checkbox
 *   ticks all; with no `id`, the clear filter rejects every row, so clearing
 *   one empties the list on screen until a reload.
 * - **`isAcknowledged`, not just `isRead`.** `normalize()` derives the row's
 *   read state *and* the badge count from `isAcknowledged`, ignoring any
 *   `isRead` passed in - so an unseen entry would badge correctly and a seen
 *   one would badge too. Setting both makes appearance and count derive from
 *   one value, and they cannot disagree.
 * - **`alertType` as a `Link`.** The row renderer reads `alertType.title` and
 *   the CTA needs an `href`, so `{title}` alone is not enough.
 * - **`details: {}`.** Read-more fetches alert details for any row which has
 *   none, and that is an alert endpoint. A pre-populated object closes the
 *   route.
 * - **`statusBadge`.** The card renders this in place of the category label an
 *   alert shows, which is how a job row reads as its own status in the grid's
 *   colors. `alertType.title` stays for the rows which have no badge.
 * - **`jobStatus`.** The raw status beside the resolved badge, so the card's CTA
 *   can gate the rerun on a failed job without parsing a translated label.
 *
 * `severity` must map to `TimeRelated` rather than `Urgent`: the category map
 * is what puts a row in the Urgent tab, which is not where a job belongs.
 *
 * @param   entry One stored notification.
 * @returns The shared panel's representation of it.
 */
export const toJobNotification = (entry?: JobNotificationEntry): Notification => ({
  id                     : entry?.jobId,
  uuid                   : jobNotificationUuid(entry?.jobId),
  name                   : entry?.name,
  createdOn              : entry?.occurredAt,
  isRead                 : entry?.hasSeen,
  isAcknowledged         : entry?.hasSeen,
  isUrgent               : false,
  isCleared              : false,
  severity               : AlertSeverity.MEDIUM,
  alertType              : {href: '', title: translate('notifications:jobStatus')},
  assetName              : entry?.name,
  alertConfigurationName : statusSubText(entry ?? {}),
  details                : {},
  statusBadge            : jobStatusBadge(entry?.status),
  jobStatus              : entry?.status
});

/**
 * Every stored notification, adapted. The panel is hydrated from this at
 * post-initialization, which is what makes the badge right on first paint
 * rather than on first open.
 */
export const toJobNotifications = (entries?: JobNotificationEntry[]): Notification[] => (
  (entries ?? []).map(toJobNotification)
);
